import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { ContentContainer } from '@/design-system/atoms/ContentContainer';
import { useEmblaCarousel } from '@/hooks/useEmblaCarousel';
import { arrayNotEmpty } from '@/utils/arrayNotEmpty';
import parseHashQueryParameters from '@/utils/parseHashQueryParameters';
import window from '@/utils/window';

import { Divider } from './components/Divider';
import { SelectedTabBottomBorder } from './components/SelectedTabBottomBorder';
import { Tab } from './components/Tab';
import styles from './Tabs.module.scss';

/**
 * Scroll to the element if it's not in the viewport
 * @param {Element} el - The element to scroll to
 * @param {Number} percentageFromViewportTop - The percentage of the elements top from the viewport top to scroll to
 * @param {Number} delay - The delay in milliseconds before scrolling
 */
const scrollIfNeeded = (el, percentageFromViewportTop = 0.2, delay = 300) => {
    const { innerHeight } = window;

    const attemptScroll = () => {
        const { top: elTop } = el.getBoundingClientRect();
        const threshold = innerHeight * percentageFromViewportTop;
        // If the element is beneath the viewport threshold or not in the viewport, scroll to it
        if (elTop > threshold || elTop < 0) {
            setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), delay);
        }
    };

    /**
     * AHSMKTG-4189: measuring `el`'s position synchronously (on the same tick the mount
     * effect runs) can happen before the browser has painted layout shifts caused by
     * content that loads above the tabs (e.g. a hero image, embla's AutoHeight plugin
     * settling). That stale measurement previously produced a false "already in view"
     * result, so no scroll was performed. Waiting two animation frames lets layout/paint
     * settle before the position is measured.
     */
    window.requestAnimationFrame(() => {
        window.requestAnimationFrame(attemptScroll);
    });
};

/**
 * Parse the url to determine the initial tab index based on the hash query parameter `tab`
 * @param {Number} activeIndex The default active tab index
 * @param {Array} tabs The tabs array frmo Tabs component
 * @returns {Object} The initial index, whether a tab in `tabs` matched the hash, and
 * whether a `tab` hash param was present at all
 *
 */
const parseUrl = (defaultActiveIndex, tabs) => {
    const urlParams = parseHashQueryParameters();
    const hadHashTab = Boolean(urlParams.tab);
    let initialIndex = defaultActiveIndex;
    let hasUrlTab = false;
    if (hadHashTab) {
        const urlTab = tabs.findIndex((tab) => tab.id === urlParams.tab);
        hasUrlTab = urlTab >= 0;
        // get hash params from url
        initialIndex = hasUrlTab ? urlTab : defaultActiveIndex;
    }

    return { hasUrlTab, initialIndex, hadHashTab };
};

export const Tabs = (props) => {
    const { className, activeIndex = 0, ariaLabel, tabs, placeholderComponent, isInEditor } = props;

    const tabsRef = React.useRef([]);
    const initialPositionRef = React.useRef();
    const [hasSetInitialTab, setHasSetInitialTab] = useState(false);
    const [selectedTabIndex, setSelectedTabIndex] = useState(activeIndex);

    // Shared by the mount effect and the hashchange listener below, so both read the hash the same way.
    const applyHashTab = React.useCallback(() => {
        const { hasUrlTab, initialIndex, hadHashTab } = parseUrl(activeIndex, tabs);
        setSelectedTabIndex(initialIndex);
        if (hasUrlTab && tabsRef.current[initialIndex]) {
            scrollIfNeeded(tabsRef.current[initialIndex], 0.2, 0);
        }

        return { matched: hasUrlTab, hadHashTab };
    }, [activeIndex, tabs]);

    // Only lock once there's nothing left to resolve (a match, or no hash to match) —
    // `tabs` can arrive as a non-empty placeholder that doesn't yet contain the hash-matched tab.
    useEffect(() => {
        if (hasSetInitialTab || !arrayNotEmpty(tabs)) {
            return;
        }

        const { matched, hadHashTab } = applyHashTab();
        if (matched || !hadHashTab) {
            setHasSetInitialTab(true);
        }
    }, [hasSetInitialTab, tabs, applyHashTab]);

    // Set the selected tab index whenever on the activeIndex prop changes
    // !! Note: this is important for when tabs are toogle in authoring mode !!
    useEffect(() => {
        if (isInEditor) {
            setSelectedTabIndex(activeIndex);
        }
    }, [setSelectedTabIndex, activeIndex, isInEditor]);

    /**
     * AHSMKTG-4189 (US3): the mount effect above only reads the URL hash once. Clicking a
     * tab updates `window.location.hash`, which the browser records as a same-document
     * history entry — using the browser's back/forward button afterward changes the hash
     * back without remounting this component, firing a native `hashchange` event that
     * nothing was listening for. That silently broke the "back button restores the
     * previously active tab (and scroll position)" acceptance criteria. Re-parsing the
     * hash on every `hashchange` keeps `selectedTabIndex` (and the scroll position via
     * `scrollIfNeeded`) in sync with subsequent browser history navigation, not just the
     * initial page load.
     */
    useEffect(() => {
        if (!arrayNotEmpty(tabs)) {
            return undefined;
        }

        window.addEventListener('hashchange', applyHashTab);
        return () => window.removeEventListener('hashchange', applyHashTab);
    }, [tabs, applyHashTab]);

    /**
     * Callback to set the selected tab index and update the url hash
     */
    const onSelectedTabIndex = (selectedIndex) => {
        const tabId = tabs.find((t, i) => i === selectedIndex)?.id;
        if (window && tabId) {
            window.location.hash = `tab=${tabId}`;
        }

        setSelectedTabIndex(selectedIndex);
    };

    const [emblaRef, emblaApi] = useEmblaCarousel({
        duration: 20,
        startIndex: selectedTabIndex,
    });

    /**
     * AHSMKTG-4189: Embla's `startIndex` option only takes effect when the carousel engine
     * is first created — it does not react to `selectedTabIndex` changing afterwards (per
     * Embla's own docs). `Tab`'s own click/keyboard handlers already call
     * `emblaApi.scrollTo(index)` for that reason, but nothing did the same for the
     * mount-time deep-link case, so the carousel's own internal position could remain out
     * of sync with the tab activated from the `#tab=<id>` hash. Keeping it in sync here
     * mirrors `Tab`'s existing behavior for every `selectedTabIndex` change, not just clicks.
     */
    useEffect(() => {
        if (emblaApi) {
            emblaApi.scrollTo(selectedTabIndex, true);
        }
    }, [emblaApi, selectedTabIndex]);

    if (!arrayNotEmpty(tabs)) {
        return null;
    }

    return (
        <div className={styles['root']}>
            <div className={classnames('embla', styles['tabs'], className)}>
                {placeholderComponent && (
                    <ContentContainer>{placeholderComponent}</ContentContainer>
                )}

                <ContentContainer
                    ref={emblaRef}
                    className={classnames('embla__viewport', styles['tabs__viewport'])}
                >
                    <div
                        role="tablist"
                        aria-label={ariaLabel}
                        className={classnames('embla__container', styles['tabs__container'])}
                    >
                        {tabs.map((t, i) => {
                            return (
                                <Tab
                                    key={i}
                                    ref={(node) => {
                                        tabsRef.current[i] = node;
                                    }}
                                    id={t.id}
                                    role="tab"
                                    aria-controls={`tab-panel--${t.id}`}
                                    aria-selected={selectedTabIndex === i ? 'true' : 'false'}
                                    tabIndex={selectedTabIndex === i ? 0 : -1}
                                    className={'embla__slide'}
                                    data-tab-index={i}
                                    title={t.title}
                                    index={i}
                                    tabsRef={tabsRef}
                                    numberOfTabs={tabs.length}
                                    emblaApi={emblaApi}
                                    setSelectedTabIndex={onSelectedTabIndex}
                                />
                            );
                        })}
                    </div>
                </ContentContainer>

                <ContentContainer>
                    <div ref={initialPositionRef} aria-hidden />
                    <SelectedTabBottomBorder
                        selectedTabIndex={selectedTabIndex}
                        tabsRef={tabsRef}
                        emblaApi={emblaApi}
                        initialPositionRef={initialPositionRef}
                    />
                    <Divider
                        tabsRef={tabsRef}
                        numberOfTabs={tabs.length}
                        initialPositionRef={initialPositionRef}
                    />
                </ContentContainer>
            </div>

            <ContentContainer className={styles['tab-panel__container']} isFullWidth={true}>
                {tabs.map((t, i) => (
                    <div
                        key={i}
                        id={`tab-panel--${t.id}`}
                        role="tabpanel"
                        aria-labelledby={t.id}
                        aria-hidden={i !== selectedTabIndex}
                        hidden={i !== selectedTabIndex}
                        tabIndex={i === selectedTabIndex ? 0 : -1}
                        className={classnames(
                            styles['tab-panel'],
                            selectedTabIndex !== i && styles['tab-panel--is-hidden'],
                        )}
                    >
                        {t.content}
                    </div>
                ))}
            </ContentContainer>
        </div>
    );
};

Tabs.propTypes = {
    activeIndex: PropTypes.number,
    ariaLabel: PropTypes.string,
    tabs: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            title: PropTypes.string,
            titleAriaLabel: PropTypes.string,
            content: PropTypes.node,
        }),
    ),
    isInEditor: PropTypes.bool,
};
